import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Button, Fetching, IconButton } from 'basic-components';
import { Clear } from 'basic-components/icons';
import { DataLakeQuery } from 'dashboard-services/api/dataLake';
import Query from 'dashboard-services/api/Query';
import { CommonService } from 'dashboard-services/services';
import { globalActions, statusBoxActions } from 'primary-components';

import { pythonEditorActions } from 'actions/modules/tools';
import getApiConfig from 'api/ApiConfig';
import PythonRuntimeHooks from 'hooks/modules/pythonEditor/runtime';
import { filesDatalakeQueries } from 'queries/modules/datalake';
import properties from 'resources/constants/properties.json';
import pythonEnvironment from 'resources/pythonEditor/environment.json';
import { sendEvent } from 'services/GAService';

import FileDetails from 'basic/FileDetails';

import Controls from './Controls';
import InitializationError from './InitializationError';
import { LIBRARY_SUGGESTIONS, SNIPPETS } from './libSuggestions';
import LLMChat from './LLMChat ';
import PackagesModal from './PackagesModal';
import ParametersModal from './ParametersModal';
import SnippetsModal from './SnippetsModal';

import { useQueryClient } from '@tanstack/react-query';
import ace from 'ace-builds/src-noconflict/ace';
import { LanguageProvider } from 'ace-linters';
import AceEditor from 'react-ace';
import { When } from 'react-if';
import { PythonProvider } from 'react-py';

import 'ace-builds/src-noconflict/snippets/python';
import 'ace-builds/src-noconflict/mode-python';
import 'ace-builds/src-noconflict/ext-inline_autocomplete';
import 'ace-builds/src-noconflict/ext-language_tools';
import 'ace-builds/src-noconflict/ext-searchbox';
import 'ace-builds/src-noconflict/theme-textmate';
import 'ace-builds/src-noconflict/theme-idle_fingers';

import './PythonEditor.scss';

const snippetsManager = ace.require("ace/snippets").snippetManager;
const { defaults: DEFAULT_PACKAGES } = pythonEnvironment
const { usePythonRuntime } = PythonRuntimeHooks

const useDownloadFile = ({ provider, fileId }) => {
  const dispatch = useDispatch(),
        [fileContent, setFileContent] = useState(),
        [fileInfo, setFileInfo] = useState({}),
        getQueryBuilder = useCallback(() =>
          new Query.Builder(dispatch(getApiConfig()))
            .withOnParse(response => Promise.resolve(response))
            .withHeader("Target", provider, true)
        , [dispatch, provider]),
        getQueryBuilderS3 = useCallback(() => 
          new Query.Builder(dispatch(getApiConfig()))
            .withOnParse(response => Promise.resolve(response))
        , [dispatch]),
        { mutateAsync: getFileLocationCall, isPending: isLocationPending } = filesDatalakeQueries.useGetFileDefinitionMutation({ getQueryBuilder, id: fileId }),
        { mutateAsync: s3GetCall, isPending: isContentPending } = filesDatalakeQueries.useGetFileContentMutation({ getQueryBuilder: getQueryBuilderS3 }),
        reqId = useMemo(() => CommonService.generateUID(), []),
        swapFetching = useCallback((reqId, isResponse) => dispatch(globalActions.handleRequest({ id: reqId, isResponse })), [dispatch]),
        downloadFile = useCallback(() => {
          if(!fileId) {
            return;
          }
          swapFetching(reqId, false)
          const getMetadataRequest = 
            getFileLocationCall()
                .then(response => {
                  const location = response.headers.get("Location")
                  return response.json().then(r => {
                    setFileInfo(r)
                    return location
                  })
                })
      
          const getFileCall = location => 
            s3GetCall({ url: location })
                .then(response => response.text())
                .then(response => setFileContent(response))
                .finally(() => swapFetching(reqId, true))
          return getMetadataRequest.then(getFileCall)
        }, [swapFetching, reqId, getFileLocationCall, s3GetCall, fileId]),
        toReturn = useMemo(() => ({ fileContent, setFileContent, fileInfo, setFileInfo, isPending: isLocationPending || isContentPending, downloadFile }), [downloadFile, fileContent, fileInfo, isContentPending, isLocationPending])

  useEffect(() => {
    downloadFile()
  }, [downloadFile])

  return toReturn
}

const libraryCompleter = {
  getCompletions: (editor, session, pos, prefix, callback) => {
    if (prefix.length === 0) { 
      callback(null, []); 
      return; 
    }

    const suggestions = Object.entries(LIBRARY_SUGGESTIONS).flatMap(([packageName, values]) => values.map(v => ({
      value: `${packageName}.${v}`,
      score: 1000,
      meta: packageName
    })))
    callback(null, suggestions.map(s => ({
      caption: s.value,
      value: s.value,
      meta: s.meta
    })));
  }
};

const EDITOR_PROPS = { $blockScrolling: true }
const editorOnLoad = (editor, languageProvider) => {
  ace.config.set("basePath", "/ace-builds");
  editor.renderer.setScrollMargin(10, 10, 0, 0);
  editor.moveCursorTo(0, 0);
  editor.setOptions({
    showPrintMargin: false,
    showGutter: true,
    enableBasicAutocompletion: true,
    enableLiveAutocompletion: true,
    enableSnippets: true,
    showFoldWidgets: true,
    displayIndentGuides: true,
    fixedWidthGutter: true,
    useSoftTabs: true,
    tabSize: 2
  });
  editor.session.setFoldStyle("markbeginend");
  languageProvider.registerEditor(editor);
  const langTools = ace.require("ace/ext/language_tools");
  editor.completers = [
    ...(editor.completers || []),
    langTools.textCompleter,
    langTools.keyWordCompleter,
    langTools.snippetCompleter,
    libraryCompleter
  ]
  snippetsManager.register(Object.values(SNIPPETS),"python")
}

const useOnSubmit = ({ editable, fileContent, fileInfo, areFileDetailsEditable, formRef, newFile, setErrors, onClose, onSubmitCallback }) => {
  const dispatch = useDispatch(),
        queryClient = useQueryClient(),
        refreshTimeoutRef = useRef(),
        fileFields = useMemo(() => Object.entries(fileInfo?.fields || {}).map(([k, v]) => ({ name: k, value: v })), [fileInfo]),
        getQueryBuilderUpload = useCallback(() => 
          new Query.Builder(dispatch(getApiConfig()))
            .withOnParse(response => Promise.resolve(response))
            .withOnError(({ response = {}, message }) => {
              setErrors(response.validationErrors)
              if(message) {
                dispatch(globalActions.getMessageStore()).error(message)
              }
            })
        , [dispatch, setErrors]),
        { uploadFileMetadata, uploadFileContent } = filesDatalakeQueries.useFileUpload({ getQueryBuilder: getQueryBuilderUpload, fileName: fileInfo?.fileName, groupName: fileInfo?.groupName, fileType: fileInfo?.fileType, fields: fileFields }),
        onSubmit = useCallback(() => {
          if(!editable) {
            return Promise.resolve();
          }
          if(areFileDetailsEditable) {
            formRef.current?.validateForForm()
            if(!formRef.current?.isValid()) {
              return Promise.resolve();
            }
          }
          const reqId = CommonService.generateUID()
          dispatch(globalActions.handleRequest({ id: reqId }))
          return uploadFileMetadata()
            .then(response => {
              const jobId = response.headers.get("X-KH-JOB-ID")
              dispatch(statusBoxActions.addJob({ jobId, displayName: fileInfo.fileName, subtitle: fileInfo.groupName }))
              return response
            })
            .then(response => response.json())
            .then(response => uploadFileContent({
              url: response.location,
              image: fileContent
            })
            .then(() => dispatch(globalActions.getMessageStore()).success(newFile ? "New file has been saved." : "New version of file has been saved."))
            .then(() => {
              onClose()
              clearTimeout(refreshTimeoutRef.current)
              // we do not want to clean this one
              refreshTimeoutRef.current = setTimeout(() => queryClient.invalidateQueries({ queryKey: DataLakeQuery.KEYS.SEARCH_FILES() }), 3000)
              onSubmitCallback?.({ name: fileInfo?.fileName, groupName: fileInfo?.groupName })
            })
            .finally(() => dispatch(globalActions.handleRequest({ id: reqId, isResponse: true })))
          )
        }, [areFileDetailsEditable, dispatch, editable, fileContent, fileInfo.fileName, fileInfo.groupName, formRef, newFile, queryClient, uploadFileContent, uploadFileMetadata, onClose, onSubmitCallback])
  return onSubmit;
}

const setUpWorker = () => {
  const worker = new Worker(new URL('./linter.js', import.meta.url), { type: "module" })
  return worker;
}

const getNgSdkEnvSnippet = ({ token, jobId, params = {} }) => `
import os
os.environ['NG_API_AUTHTOKEN'] = '${token}'
os.environ['NG_API_ENDPOINT'] = '${properties.baseUrl.api}'
os.environ['JOBID'] = '${jobId}'
${Object.entries(params).map(([name, value]) => 
`os.environ['${name}'] = '${value || ""}'
`
).join("")}
`

// TODO Display csv as result
const PythonEditor = () => {
  const dispatch = useDispatch(),
        { id: fileId, editable, jobId, provider, groupName, newFile = false, taskId, onSubmitCallback } = useSelector(state => state.toolsState.pythonEditorState),
        defaultUserPackages = useSelector(state => state.myAccountState.settingsState.defaultPythonPackages),
        token = useSelector(state => state.primaryComponentsState.loginState.token),
        defaultTaskParams = useSelector(state => state.datascienceState.creatorState.builderState.tasks?.[taskId]?.params),
        { fileContent, setFileContent, fileInfo, setFileInfo, isPending, downloadFile } = useDownloadFile({ provider, fileId }),
        [areFileDetailsEditable, setAreFileDetailsEditable] = useState(newFile),
        [areParamsOpen, setAreParamsOpen] = useState(false),
        closeParameters = useCallback(() => setAreParamsOpen(false), []),
        [isSnippetsOpen, setIsSnippetsOpen] = useState(false),
        closeSnippets = useCallback(() => setIsSnippetsOpen(false), []),
        [isWrapEnabled, setIsWrapEnabled] = useState(false),
        [isDone, setIsDone] = useState(false),
        [errors, setErrors] = useState([]),
        [chatContextVersion, setChatContextVersion] = useState(0),
        [isAddPackageOpen, setIsAddPackageOpen] = useState(false),
        [messages, setMessages] = useState([]),
        closeAddPackage = useCallback(() => setIsAddPackageOpen(false), []),
        [taskParams, setTaskParams] = useState(defaultTaskParams),
        taskParamsAsObject = useMemo(() => Object.fromEntries((taskParams || []).map(param => [param.name, typeof param.value === "object" || Array.isArray(param.value) ? JSON.stringify(param.value) : param.value || ""])), [taskParams]),
        onClose = useCallback(() => dispatch(pythonEditorActions.clear()), [dispatch]),
        [worker] = useState(setUpWorker),
        languageProvider = useMemo(() => LanguageProvider.create(worker), [worker]),
        formRef = useRef(),
        onSubmit = useOnSubmit({ editable, fileContent, fileInfo, areFileDetailsEditable, formRef, newFile, setErrors, onClose, onSubmitCallback }),
        [userPackages, setUserPackages] = useState(defaultUserPackages || DEFAULT_PACKAGES),
        { runPython: runPythonRaw, stdout, stderr, isLoading, isRunning, interruptExecution, isReady, initializationError, restartKernel: restartPythonKernel } = usePythonRuntime({ packages: userPackages, environment: pythonEnvironment }),
        runPython = useCallback(async fileContent => {
          setIsDone(false)
          const completed = await runPythonRaw({ code: getNgSdkEnvSnippet({ token, jobId, params: taskParamsAsObject }) + fileContent })
          if(completed) {
            setIsDone(true)
          }
        }, [jobId, runPythonRaw, taskParamsAsObject, token]),
        restartKernel = useCallback(() => {
          setIsDone(false)
          restartPythonKernel()
        }, [restartPythonKernel]),
        editorRef = useRef(),
        addSnippet = useCallback(snippet => {
          if(!editorRef.current?.editor || !snippet?.content) {
            return;
          }
          snippetsManager.insertSnippet(editorRef.current.editor, snippet.content)
          closeSnippets()
        }, [closeSnippets]),
        onLoad = useCallback(props => editorOnLoad(props, languageProvider), [languageProvider]),
        renderAceEditor = useCallback(() => (
          <AceEditor
              editorProps={EDITOR_PROPS}
              focus
              fontSize={12}
              height="100%"
              highlightActiveLine
              lineHeight={16}
              // maxLines={Infinity} - this break scrolling
              minLines={(window.innerHeight - 132 - 302 - 28) / 16}
              mode="python"
              name="Python"
              onChange={setFileContent}
              onLoad={onLoad}
              readOnly={!editable || !!provider}
              ref={editorRef}
              tabSize={2}
              theme={CommonService.isDarkMode() ? 'idle_fingers' : 'textmate'}
              value={fileContent}
              width="100%"
              wrapEnabled={isWrapEnabled}
          />
        ), [editable, fileContent, isWrapEnabled, setFileContent, provider, onLoad]),
        newFileHeader = useMemo(() => newFile || fileInfo?.fileName ? (
          <FileDetails
              areFileDetailsEditable={areFileDetailsEditable}
              className="ng-python-editor__header__title"
              editable={editable && !provider}
              errors={errors}
              extension="py"
              fileInfo={fileInfo}
              groupNameLoc={groupName}
              newFile={newFile}
              ref={formRef}
              setAreFileDetailsEditable={setAreFileDetailsEditable}
              setFileInfo={setFileInfo}
          />
        ) : <div className="ng-python-editor__header__title"/>, [newFile, fileInfo, areFileDetailsEditable, editable, provider, errors, groupName, setFileInfo]),
        addPackages = useCallback(packages => {
          setIsDone(false)
          setUserPackages(packages)
        }, []),
        reloadFileContent = useCallback(() => 
          Promise.resolve(downloadFile())
            .finally(() => setChatContextVersion(version => version + 1))
        , [downloadFile]),
        isChatEditable = editable && !provider

  useEffect(() => {
    if(!fileId) {
      return;
    }
    setChatContextVersion(version => version + 1)
  }, [fileId, provider])

  useEffect(() => {
    const event = {
      category: "view_item",
      action: "python_editor",
      label: "tool",
    };
    dispatch(sendEvent(event))
  }, [dispatch])

  useEffect(() => () => {
    worker.terminate()
  }, [worker])

  return (
    <PythonProvider
        autoImportPackages
    >
      <div className="ng-python-editor">
        <div className="ng-python-editor__header">
          {newFileHeader}
          <div className="ng-python-editor__header__close">
            <div className="ng-python-editor__header__close__text">
              Close editor
            </div>
            <IconButton
                className="ng-python-editor__header__close__button"
                onClick={onClose}
            >
              <Clear
                  color="grey-medium"
                  height={16}
                  width={16}
              />
            </IconButton>
          </div>
        </div>
        <div className="ng-python-editor__body">
          {renderAceEditor()}
        </div>
        {isChatEditable && (
          <div className="ng-python-editor__chat-layer">
            <LLMChat 
                content={fileContent}
                contentSyncToken={chatContextVersion}
                messages={messages}
                setContent={setFileContent}
                setMessages={setMessages}
                taskParams={taskParamsAsObject}
            />
          </div>
        )}
        <div className="ng-python-editor__faker"/>
        <div className="ng-python-editor__footer">
          <Controls
              canResetFile={!!fileId}
              editorRef={editorRef}
              fileContent={fileContent}
              hasInitializationError={!!initializationError}
              interruptExecution={interruptExecution}
              isFilePending={isPending}
              isLoading={isLoading}
              isReady={isReady}
              isRunning={isRunning}
              isWrapEnabled={isWrapEnabled}
              resetFileContent={reloadFileContent}
              restartKernel={restartKernel}
              runPython={runPython}
              setAreParamsOpen={setAreParamsOpen}
              setIsAddPackageOpen={setIsAddPackageOpen}
              setIsSnippetsOpen={setIsSnippetsOpen}
              setIsWrapEnabled={setIsWrapEnabled}
              taskId={taskId}
          />
          <div className="ng-python-editor__footer__output">
            <Fetching
                loading={isLoading || isRunning}
                size={20}
            />
            {initializationError && (
              <InitializationError
                  error={initializationError}
                  isPending={isLoading}
              />
            )}
            <When condition={stderr && !isLoading}>
              <code className="ng-python-editor__footer__output__error">{stderr}</code>
            </When>
            <When condition={!isRunning && isDone && !stderr && !stdout && !isLoading && isReady}>
              <code className="ng-python-editor__footer__output__return">Code executed - no issues.</code>
            </When>
            <When condition={isRunning && !stdout && !stderr}>
              <code className="ng-python-editor__footer__output__return">Running...</code>
            </When>
            <When condition={isLoading && !initializationError}>
              <code className="ng-python-editor__footer__output__return">Installing packages...</code>
            </When>
            <When condition={(stdout || (!stderr && !isRunning)) && !isLoading && isReady}>
              <code className="ng-python-editor__footer__output__return">{(stdout || "Run the code to see the results")}</code>
            </When>
          </div>
          <div className="ng-python-editor__footer__buttons">
            <Button 
                onClick={onClose}
                variant="text"
            >
              Cancel
            </Button>
            <Button
                disabled={!editable || !!provider || isPending}
                onClick={onSubmit}
                size="small"
                type="submit"
                variant="primary"
            >
              Save
            </Button>
          </div>
        </div>
        <When condition={isAddPackageOpen}>
          {() => (
            <PackagesModal
                DEFAULT_PACKAGES={DEFAULT_PACKAGES}
                onClose={closeAddPackage}
                onSubmit={addPackages}
                userPackages={userPackages}
            />
          )}
        </When>
        <When condition={areParamsOpen}>
          {() => (
            <ParametersModal
                defaultTaskParams={taskParams}
                onClose={closeParameters}
                onSubmit={setTaskParams}
                taskId={taskId}
            />
          )}
        </When>
        <When condition={isSnippetsOpen}>
          {() => (
            <SnippetsModal
                addSnippet={addSnippet}
                onClose={closeSnippets}
            />
          )}
        </When>
      </div>
    </PythonProvider>
  )
}
export default PythonEditor;
